<template>
  <div
    id="settings-js"
    class="mb-10"
    style="max-width: 100%;"
  >
    <div
      v-if="accountIsStarter && code.length === 0 && editorLoaded"
      class="info-banner grid grid-cols-2"
    >
      <div>
        <p class="mb-2">
          <strong>Want to add custom JavaScript?</strong>
        </p>
        Upgrade to add the option to enable custom code across all of your apps
      </div>
      <div class="flex justify-end items-center">
        <a
          :href="billingPlansUrl"
          target="_blank"
          class="button small bg-gradient-primary rounded-lg
          border-0 p-3 text-base text-white leading-4 font-medium w-[220px] float-right"
        >
          <FontAwesome
            name="diamond"
            class="mr-2"
          />
          View Plans and Upgrade
        </a>
        <a
          class="text-subtle hover:text-default underline ml-4 items-center flex"
          target="_blank"
          rel="noreferrer"
          href="https://docs.knack.com/v3/docs/getting-help-with-custom-code"
        >
          <span>Learn More</span>
          <Icon
            type="arrow-top-right-on-square"
            class="h-4 w-4 ml-2"
          />
        </a>
      </div>
    </div>
    <div v-else>
      <form id="code-editor-form-js mb-0">
        <div
          v-if="accountIsStarter && editorLoaded"
          class="info-banner grid grid-cols-2 bg-[#FEF4D7]"
        >
          <div>
            <p class="mb-2">
              <strong>Your custom Javascript is currently read-only</strong>
            </p>
            The below custom Javascript is not being applied to your Live App.
            Upgrade to edit and manage custom code across all your apps.
          </div>
          <div class="flex justify-end items-center">
            <a
              :href="billingPlansUrl"
              target="_blank"
              class="button small bg-gradient-primary rounded-lg
              border-0 p-3 text-base text-white leading-4 font-medium w-[220px] float-right"
            >
              <FontAwesome
                name="diamond"
                class="mr-2"
              />
              View Plans and Upgrade
            </a>
            <a
              class="text-subtle hover:text-default underline ml-4 items-center flex"
              target="_blank"
              rel="noreferrer"
              href="https://docs.knack.com/v3/docs/getting-help-with-custom-code"
            >
              <span>Learn More</span>
              <Icon
                type="arrow-top-right-on-square"
                class="h-4 w-4 ml-2"
              />
            </a>
          </div>
        </div>
        <p
          v-else
          class="instructions-js text-default text-base mb-6 max-w-xl"
        >
          Custom JavaScript will be called after the Knack app has been loaded.
          <a
            href="https://docs.knack.com/reference/using-javascript-with-knack"
            target="_blank"
            class="underline text-default"
            style="display: inline-block"
          >
            View our dev docs
          </a> to learn more.
        </p>
        <CodeEditor
          data-testid="custom-code-area"
          language="javascript"
          :editor-options="editorOptions"
          @save="onSave"
        />
      </form>
      <div class="flex mt-3">
        <button
          data-testid="save-code-btn"
          class="button medium save bg-gradient-primary rounded-lg border-0 p-3 text-base leading-4 font-medium"
          :disabled="accountIsStarter"
          :title="saveButtonTitle"
          @click="onSave"
        >
          Save Code
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { eventBus } from '@/store/bus';
import CodeEditor from '@/components/util/CodeEditor';
import RequestUtils from '@/components/util/RequestUtils';
import { trackSecondaryEvent } from '@/lib/rudderstack-helper';
import FontAwesome from '@/components/ui/FontAwesome.vue';
import Icon from '@/components/ui/Icon.vue';

export default {
  components: {
    CodeEditor,
    FontAwesome,
    Icon,
  },
  data() {
    return {
      code: '',
      editorLoaded: false,
    };
  },
  computed: {
    ...mapGetters([
      'app',
    ]),
    accountIsStarter() {
      return this.app?.account.product_plan.id.includes('starter_') ?? false;
    },
    editorOptions() {
      return {
        roundedSelection: false,
        scrollBeyondLastLine: false,
        readOnly: this.accountIsStarter,
        tabSize: 2,
        theme: 'vs-dark',
      };
    },
    saveButtonTitle() {
      return this.accountIsStarter ? 'Upgrade your plan to enable this feature' : 'Save Code';
    },
    billingPlansUrl() {
      return `${process.env.VUE_APP_DASHBOARD_URL}/settings/billing/plans`;
    },
  },
  created() {
    eventBus.$on('javascriptCodeEditor:update', (newCode) => {
      this.code = encodeURIComponent(newCode);
      this.editorLoaded = true;
    });
  },
  beforeUnmount() {
    eventBus.$off('javascriptCodeEditor:update');
  },
  methods: {
    onSave() {
      this.commitRequest({
        request: () => {
          const update = {
            settings: this.app.get('settings'),
          };

          update.settings.javascript = this.code;

          return this.app.update(update);
        },
        onSuccess: () => {
          trackSecondaryEvent('javascript_custom', {}, 10); // 10 seconds throttle for custom JS changes
        },
      });
    },
  },
  mixins: [
    RequestUtils,
  ],
};
</script>

<style lang="scss">
#code-editor-form-js {
  margin-bottom: 1.25em;
  max-width: 100%;
  width: 100%;
}

.instructions-js {
  margin-bottom: 1em;
}

.info-banner {
  width: 100%;
  padding: 12px 16px;
  align-items: center;
  border-radius: 8px;
  background-color: #F5F4F5;
}

</style>
